import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { InsuranceCardDocumentType } from "@/src/models/constants/document-types";
import { coverageSchema } from "@/src/models/coverage";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const insuranceCardTypeDisplay: Record<InsuranceCardDocumentType, string> = {
  [InsuranceCardDocumentType.COMMUNICATION]: "Insurance Communication",
  [InsuranceCardDocumentType.INSURANCE_CARD]: "Insurance Card",
  [InsuranceCardDocumentType.LETTER_OF_ASSURANCE]: "Letter of Assurance",
};

export const insuranceCardTypeSchema = z.nativeEnum(InsuranceCardDocumentType);

const baseInsuranceCardFields = {
  patient: patientMinimalSchema,
  coverage: coverageSchema,
};

const extendedInsuranceCardSchema = z.object({
  isAdw: isAdwSchema,
  insuranceCompanyName: z.string().nullable(),
  location: locationSchema.nullable(),
  provider: practitionerSchema.nullable(),
  isInsuranceAuditRequest: z.boolean().nullable(),
  containsMultiplePatients: z.boolean().nullable(),
  ...DocumentDateSchema.shape,
});

const insuranceCardCustomFields: Record<InsuranceCardDocumentType, Record<string, z.ZodTypeAny>> = {
  [InsuranceCardDocumentType.COMMUNICATION]: extendedInsuranceCardSchema.pick({
    isAdw: true,
    dateReceived: true,
    dateOfEvent: true,
    insuranceCompanyName: true,
    isInsuranceAuditRequest: true,
    containsMultiplePatients: true,
  }).shape,
  [InsuranceCardDocumentType.INSURANCE_CARD]: extendedInsuranceCardSchema.pick({
    isAdw: true,
    dateReceived: true,
    dateOfEvent: true,
    insuranceCompanyName: true,
  }).shape,
  [InsuranceCardDocumentType.LETTER_OF_ASSURANCE]: extendedInsuranceCardSchema.pick({
    dateReceived: true,
    dateOfEvent: true,
    insuranceCompanyName: true,
  }).shape,
};

const makeInsuranceCardSchema = (type: InsuranceCardDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseInsuranceCardFields,
    ...customFields,
  });

export const insuranceCardSchemas: Record<InsuranceCardDocumentType, z.ZodObject<any>> = (
  Object.values(InsuranceCardDocumentType) as InsuranceCardDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeInsuranceCardSchema(type, insuranceCardCustomFields[type]);

    return acc;
  },
  {} as Record<InsuranceCardDocumentType, z.ZodObject<any>>
);

const insuranceCardSchemasArray = (Object.values(InsuranceCardDocumentType) as InsuranceCardDocumentType[]).map(
  (type) => insuranceCardSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyInsuranceCardSchema = z.discriminatedUnion("type", insuranceCardSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type InsuranceCard = ExtractedDocumentFields<
  InsuranceCardDocumentType,
  typeof extendedInsuranceCardSchema,
  typeof baseInsuranceCardFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const insuranceCardShape: InsuranceCard = {
  id: "",
  type: InsuranceCardDocumentType.INSURANCE_CARD,
  patient: null,
  coverage: null,
  insuranceCompanyName: null,
  dateReceived: null,
  dateOfEvent: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
